import * as React from 'react';
import { AbsenceFormData, AbsenceType } from '../services/AbsenceService';
import styles from './Absences.module.css';

interface EmployeeOption {
  id: number;
  text: string;
}

interface AbsenceAddModalProps {
  addData: AbsenceFormData;
  setAddData: React.Dispatch<React.SetStateAction<AbsenceFormData>>;
  empSelect: EmployeeOption[];
  onClose: () => void;
  onSave: () => void;
}

export function AbsenceAddModal({
  addData,
  setAddData,
  empSelect,
  onClose,
  onSave,
}: AbsenceAddModalProps): React.JSX.Element {
  return (
    <div
      data-testid="add-modal-overlay"
      className={styles.modalOverlay}
      onClick={onClose}
    >
      <div
        data-testid="add-modal-content"
        onClick={(e) => e.stopPropagation()}
        className={styles.modalContent}
      >
        {/* Header */}
        <div className={styles.modalHeader}>
          <h2 className={styles.modalTitle}>New Absence</h2>
          <button onClick={onClose} className={styles.modalCloseBtn}>
            &#10005;
          </button>
        </div>

        {/* Form */}
        <div className={styles.modalForm}>
          {/* Employee & Type */}
          <div className={styles.modalFormRow}>
            <div className={styles.modalFormCol}>
              <label className={styles.glassLabel}>Employee</label>
              <select
                value={addData.employee}
                onChange={(e) => setAddData({ ...addData, employee: e.target.value })}
                className={styles.glassInput}
                data-testid="absence-employee-select"
              >
                <option value="">Select Employee</option>
                {empSelect.map(emp => (
                  <option key={emp.id} value={emp.id}>
                    {emp.text}
                  </option>
                ))}
              </select>
            </div>
            <div className={styles.modalFormCol}>
              <label className={styles.glassLabel}>Absence Type</label>
              <select
                value={addData.absence_type}
                onChange={(e) => setAddData({ ...addData, absence_type: e.target.value as AbsenceType })}
                className={styles.glassInput}
                data-testid="absence-type-select"
              >
                <option value="HOLIDAY">Holiday</option>
                <option value="OVERTIME">Overtime</option>
                <option value="SICK">Sick</option>
                <option value="SICK_CHILD">Sick Child</option>
                <option value="EXAM">Exam</option>
                <option value="WISH_OFF">Wish Off</option>
              </select>
            </div>
          </div>

          {/* Start Date & End Date */}
          <div className={styles.modalFormRow}>
            <div className={styles.modalFormCol}>
              <label className={styles.glassLabel}>Start Date</label>
              <input
                type="date"
                value={addData.start_date}
                onChange={(e) => setAddData({ ...addData, start_date: e.target.value })}
                className={styles.glassInput}
                data-testid="absence-start-date-input"
              />
            </div>
            <div className={styles.modalFormCol}>
              <label className={styles.glassLabel}>End Date</label>
              <input
                type="date"
                value={addData.end_date}
                onChange={(e) => setAddData({ ...addData, end_date: e.target.value })}
                className={styles.glassInput}
                data-testid="absence-end-date-input"
              />
            </div>
          </div>

          {/* Half Day Toggles */}
          <div className={styles.halfDayRow}>
            <label className={styles.halfDayLabel}>
              <input
                type="checkbox"
                checked={!addData.full_day_start}
                onChange={(e) => setAddData({ ...addData, full_day_start: !e.target.checked })}
                style={{ width: 'auto', accentColor: '#E7007D' }}
              />
              Erster Tag halber Tag
            </label>
            <label className={styles.halfDayLabel}>
              <input
                type="checkbox"
                checked={!addData.full_day_end}
                onChange={(e) => setAddData({ ...addData, full_day_end: !e.target.checked })}
                style={{ width: 'auto', accentColor: '#E7007D' }}
              />
              Letzter Tag halber Tag
            </label>
          </div>

          {/* Time fields for partial days */}
          {(!addData.full_day_start || !addData.full_day_end) && (
            <div className={styles.timeFieldsRow}>
              {!addData.full_day_start && (
                <div className={styles.timeFieldGroup}>
                  <div className={styles.timeFieldCol}>
                    <label className={styles.glassLabel}>Von</label>
                    <input
                      type="time"
                      value={addData.start_day_start_time}
                      onChange={(e) => setAddData({ ...addData, start_day_start_time: e.target.value })}
                      className={styles.glassInput}
                    />
                  </div>
                  <div className={styles.timeFieldCol}>
                    <label className={styles.glassLabel}>Bis</label>
                    <input
                      type="time"
                      value={addData.start_day_end_time}
                      onChange={(e) => setAddData({ ...addData, start_day_end_time: e.target.value })}
                      className={styles.glassInput}
                    />
                  </div>
                </div>
              )}
              {!addData.full_day_end && addData.start_date !== addData.end_date && (
                <div className={styles.timeFieldGroup}>
                  <div className={styles.timeFieldCol}>
                    <label className={styles.glassLabel}>Von</label>
                    <input
                      type="time"
                      value={addData.end_day_start_time}
                      onChange={(e) => setAddData({ ...addData, end_day_start_time: e.target.value })}
                      className={styles.glassInput}
                    />
                  </div>
                  <div className={styles.timeFieldCol}>
                    <label className={styles.glassLabel}>Bis</label>
                    <input
                      type="time"
                      value={addData.end_day_end_time}
                      onChange={(e) => setAddData({ ...addData, end_day_end_time: e.target.value })}
                      className={styles.glassInput}
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Reason */}
          <div>
            <label className={styles.glassLabel}>Reason (optional)</label>
            <textarea
              value={addData.reason_note}
              onChange={(e) => setAddData({ ...addData, reason_note: e.target.value })}
              placeholder="Add a note..."
              className={styles.glassTextarea}
              data-testid="absence-reason-textarea"
            />
          </div>
        </div>

        {/* Footer Buttons */}
        <div className={styles.modalFooter}>
          <button
            data-testid="add-modal-cancel-button"
            onClick={onClose}
            className={styles.cancelBtn}
          >
            Cancel
          </button>
          <button
            data-testid="add-modal-save-button"
            onClick={onSave}
            className={styles.saveBtn}
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
}
